import type { FormEvent } from "react";

import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { GameEditorFields } from "./ScheduleEditorFields";
import type { GameEditorValue } from "./scheduleEditor";
import type { ActiveSchedulePlayer } from "./scheduleTypes";

export function GameEditorCard({
  value,
  players,
  editing,
  editingActive,
  editingSeriesGame,
  serverBusy,
  pending = serverBusy,
  unknown = false,
  onChange,
  onCancel,
  onSubmit,
}: {
  value: GameEditorValue;
  players: ActiveSchedulePlayer[];
  editing: boolean;
  editingActive: boolean;
  editingSeriesGame: boolean;
  serverBusy: boolean;
  pending?: boolean;
  unknown?: boolean;
  onChange: (value: GameEditorValue) => void;
  onCancel: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <Card>
      <CardContent className="grid gap-3 pt-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="m-0 font-mono text-sm font-bold tracking-[0.1em] text-warning uppercase">
            {editing ? "EDIT GAME" : "ADD GAME"}
          </h2>
          <Button
            type="button"
            size="xs"
            variant="ghost"
            onClick={onCancel}
            disabled={pending}
          >
            CANCEL
          </Button>
        </div>
        <form
          className="grid gap-3"
          onSubmit={onSubmit}
          aria-busy={pending || undefined}
        >
          <fieldset disabled={pending} className="contents">
            <GameEditorFields
              value={value}
              players={players}
              activeLocked={editingActive}
              opponentLocked={editingActive || editingSeriesGame}
              onChange={onChange}
            />
            {editingActive ? (
              <p className="m-0 text-xs text-foreground-muted">
                Live games can only be rescheduled by changing their date and
                time.
              </p>
            ) : editingSeriesGame ? (
              <p className="m-0 text-xs text-foreground-muted">
                The opponent is shared by the playoff series. Edit the series to
                change it.
              </p>
            ) : null}
            <Button
              type="submit"
              size="scoring"
              pending={pending}
              pendingLabel={
                unknown ? "CHECKING…" : editing ? "SAVING…" : "CREATING…"
              }
            >
              {editing ? "SAVE GAME" : "ADD TO SCHEDULE"}
            </Button>
          </fieldset>
        </form>
      </CardContent>
    </Card>
  );
}
